import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Shield, Users, TrendingUp, Link2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet';

// Custom hook to ensure noindex meta tag is added (workaround for react-helmet in React 18)
function useNoIndexMeta() {
  useEffect(() => {
    // Check if meta tag already exists
    let metaTag = document.querySelector('meta[name="robots"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'robots';
      document.head.appendChild(metaTag);
    }
    metaTag.content = 'noindex, nofollow';
    
    return () => {
      // Cleanup on unmount
      const tag = document.querySelector('meta[name="robots"]');
      if (tag) {
        tag.remove();
      }
    };
  }, []);
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AffiliateRegisterPage() {
  // Ensure noindex meta tag is added
  useNoIndexMeta();
  
  const [formData, setFormData] = useState({
    nom: '', prenom: '', email: '', password: '', telephone: '', entreprise: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/affiliates/register`, formData);
      setSuccess(res.data);
      toast.success('Inscription envoyée avec succès !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Inscription Partenaire - Assistance Rédactionnelle</title>
        </Helmet>
        <div className="py-16 sm:py-24">
          <div className="container-narrow max-w-xl">
            <Card className="p-8 text-center" data-testid="affiliate-success-card">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-foreground mb-4">
                Inscription enregistrée
              </h1>
              <p className="text-muted-foreground mb-6">
                Votre demande de partenariat a été soumise. Notre équipe l'examinera dans les 24 à 48 heures.
              </p>
              <Separator className="my-6" />
              <div className="text-left space-y-3 bg-secondary/50 p-4 rounded-lg">
                <p className="text-sm font-medium text-foreground">Votre lien personnel (après activation) :</p>
                <code className="block text-sm bg-card p-3 rounded border border-border break-all" data-testid="affiliate-link-preview">
                  {window.location.origin}/r/{success.slug}
                </code>
                <p className="text-xs text-muted-foreground">
                  Vous pouvez déjà accéder à votre espace partenaire.
                </p>
              </div>
              <Button
                className="w-full mt-6 bg-primary hover:bg-primary/90 text-primary-foreground h-11 font-semibold"
                onClick={() => window.location.href = '/connexion-partenaire'}
                data-testid="go-to-login-button"
              >
                Accéder à mon espace partenaire
              </Button>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Devenir Partenaire - Assistance Rédactionnelle</title>
      </Helmet>

      {/* Header */}
      <header className="border-b border-border bg-white">
        <div className="container-narrow py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-accent" />
            <span className="text-lg font-semibold text-primary">Assistance Rédactionnelle</span>
          </div>
          <Badge variant="outline" className="text-xs">Programme Partenaires</Badge>
        </div>
      </header>

      <div className="py-12 sm:py-16">
        <div className="container-narrow max-w-3xl">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-foreground mb-4" data-testid="affiliate-page-title">
              Devenez partenaire
            </h1>
            <p className="text-lg text-muted-foreground max-w-lg mx-auto">
              Recommandez notre service de rédaction administrative et percevez une commission sur chaque client référé.
            </p>
          </div>

          {/* Avantages */}
          <div className="grid sm:grid-cols-3 gap-4 mb-12">
            <Card className="p-5 text-center">
              <TrendingUp className="w-6 h-6 text-accent mx-auto mb-3" />
              <p className="font-semibold text-sm text-foreground mb-1">Commission attractive</p>
              <p className="text-xs text-muted-foreground">Taux personnalisé par partenaire</p>
            </Card>
            <Card className="p-5 text-center">
              <Link2 className="w-6 h-6 text-accent mx-auto mb-3" />
              <p className="font-semibold text-sm text-foreground mb-1">Lien personnel</p>
              <p className="text-xs text-muted-foreground">URL courte et personnalisable</p>
            </Card>
            <Card className="p-5 text-center">
              <Shield className="w-6 h-6 text-accent mx-auto mb-3" />
              <p className="font-semibold text-sm text-foreground mb-1">Suivi transparent</p>
              <p className="text-xs text-muted-foreground">Tableau de bord en temps réel</p>
            </Card>
          </div>

          {/* Form */}
          <Card className="p-8" data-testid="affiliate-register-form">
            <h2 className="text-xl font-semibold text-foreground mb-6">Formulaire d'inscription</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Prénom *</label>
                  <Input
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    required
                    placeholder="Jean"
                    data-testid="affiliate-prenom-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Nom *</label>
                  <Input
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    placeholder="Dupont"
                    data-testid="affiliate-nom-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email *</label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="jean.dupont@exemple.ch"
                  data-testid="affiliate-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Mot de passe *</label>
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  placeholder="Minimum 6 caractères"
                  data-testid="affiliate-password-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Téléphone</label>
                  <Input
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="+41 79 123 45 67"
                    data-testid="affiliate-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Entreprise</label>
                  <Input
                    name="entreprise"
                    value={formData.entreprise}
                    onChange={handleChange}
                    placeholder="Nom de votre entreprise"
                    data-testid="affiliate-company-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 font-semibold"
                data-testid="affiliate-submit-button"
              >
                {submitting ? 'Envoi en cours...' : 'Soumettre ma candidature'}
              </Button>
            </form>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Déjà partenaire ?{' '}
            <a href="/connexion-partenaire" className="text-accent hover:underline font-medium">
              Connectez-vous
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
